import React, { ChangeEvent, useState } from 'react'

import { Tab } from '@headlessui/react'
import { FormikValues } from 'formik'

import { FileSelector } from './FileSelector'
import { FilesRenameForm } from './FilesRenameForm'
import { useAppDispatch } from '@/client/hooks'
import Spinner from '@/components/Spinner'
import {
  HEO_DOCS_FOLDER,
  SERVICING_FOLDER,
  setActiveModal,
} from '@/features/FileManager/fileManagerSlice'
import {
  useDocumentUploadFinishMutation,
  useDocumentUploadStartMutation,
} from '@/services/documents'
import { useUploadFileMutation } from '@/services/s3'
import { UserDocument } from '@/types/unlock'

interface FileUploadModal {
  applicantIds: number[]
  selectedFolder: string
  selectedParentFolder?: string
  preSelectedFiles?: File[]
  setPreSelectedFiles?: React.Dispatch<React.SetStateAction<File[]>>
  onFinishUploadCallback?: (results: UserDocument[]) => void
  enableFilesDrop?: boolean
  multiple?: boolean
  promptMessage?: string
  forcedTag?: string
}

export function FileUploadModal({
  applicantIds,
  selectedFolder,
  selectedParentFolder,
  preSelectedFiles,
  setPreSelectedFiles,
  onFinishUploadCallback,
  enableFilesDrop,
  promptMessage,
  multiple = true,
  forcedTag,
}: FileUploadModal) {
  const dispatch = useAppDispatch()

  const tabMap = {
    SELECT_FILES: 0,
    RENAME_FILES: 1,
    UPLOADING: 2,
  }

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [filesToUpload, setFilesToUpload] = useState<File[]>(() => {
    if (preSelectedFiles?.length > 0) {
      setSelectedIndex(tabMap.RENAME_FILES)
      return preSelectedFiles
    }
    return []
  })

  const [documentUploadStart] = useDocumentUploadStartMutation()
  const [documentUploadFinish] = useDocumentUploadFinishMutation()
  const [uploadFile] = useUploadFileMutation()
  const [isUploading, setIsUploading] = useState(false)

  const onSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    let files = []
    if (multiple) {
      files = Array.from(e.currentTarget.files)
    } else {
      files = [e.currentTarget.files[0]]
    }
    setFilesToUpload(files)
    setSelectedIndex(tabMap.RENAME_FILES)
  }

  const hasFilesToUpload = filesToUpload.length > 0

  const handleFileRemoval = (index: number) => {
    const updatedFilesToUpload = [...filesToUpload.filter((_, i) => i !== index)]
    setFilesToUpload(updatedFilesToUpload)
    setPreSelectedFiles?.(updatedFilesToUpload) // Remove from dnd files
    if (filesToUpload.length === 1) {
      setSelectedIndex(tabMap.SELECT_FILES)
    }
  }

  const handleFilesRename = (values: FormikValues) => {
    // Beware of deleted files from the list
    const renamedFiles = values.filesToUpload.map((file, index) => {
      const { category, name, identifier, date } = values.renamedFiles[index]
      const fileExt = file.name.split('.').pop()
      const newName = identifier
        ? `${category} - ${name} - ${identifier} - ${date}`
        : `${category} - ${name} - ${date}`
      return new File([file], `${newName}.${fileExt}`, {
        type: file.type,
        lastModified: file.lastModified,
      })
    })
    setSelectedIndex(tabMap.UPLOADING)
    uploadRenamedFiles({ files: renamedFiles, filesContext: values.renamedFiles })
  }

  const isFileKindCustomerVisible = (fileKind: string) => {
    const customerVisibleFileKinds = [
      'government-id--drivers-license',
      'government-id--passport',
      'government-id--green-card',
      'property--home-owners-insurance-policy',
    ]

    const isUnderHecDocs = [selectedFolder, selectedParentFolder].includes(HEO_DOCS_FOLDER)

    return isUnderHecDocs && customerVisibleFileKinds.includes(fileKind)
  }

  type IFilesContext = {
    name: string
    category: string
    identifier: string
    tag: string
    kind?: string
  }

  type uploadRenamedFilesProps = {
    files: File[]
    filesContext?: Partial<IFilesContext[]>
  }

  async function uploadRenamedFiles({ files, filesContext }: uploadRenamedFilesProps) {
    const application_profile = applicantIds[0]
    const defaultTag = selectedParentFolder
      ? `${selectedParentFolder}/${selectedFolder}`
      : selectedFolder

    // documents uploaded under servicing folder are customer visible by default
    const isUnderServicing = [selectedFolder, selectedParentFolder].includes(SERVICING_FOLDER)

    setIsUploading(true)

    const uploadResults: UserDocument[] = []

    const promises = files.map(async (file, index) => {
      const kind = filesContext[index]?.kind || ''
      const payload = {
        file_name: file.name,
        application_profile,
        tag: forcedTag ? forcedTag : filesContext[index]?.tag || defaultTag,
        customer_visible: isUnderServicing || isFileKindCustomerVisible(kind),
      }

      if (isUnderServicing) {
        payload['kind'] = 'servicing--document-uploads'
      } else if (kind) {
        payload['kind'] = kind
      }

      const { url, uuid } = await documentUploadStart(payload).unwrap()
      await uploadFile({ url, file })
      const result = await documentUploadFinish(uuid).unwrap()
      uploadResults.push(result)
    })

    await Promise.all(promises)
    setPreSelectedFiles?.([]) // Clear drag and dropped files
    dispatch(setActiveModal(null))
    onFinishUploadCallback?.(uploadResults)
    setIsUploading(false)
  }

  const tabClassnames = `
    ui-selected:border-b-2
    ui-selected:border-b-blue-500
    ui-selected:text-blue-800
    ui-selected:font-semibold
    ui-not-selected:text-gray-400
    ui-not-selected:border-b-2
    ui-not-selected:border-transparent
    px-7 pb-3
  `

  return (
    <Tab.Group
      selectedIndex={selectedIndex}
      onChange={setSelectedIndex}
    >
      <Tab.List className='flex gap-x-1'>
        <Tab
          className={tabClassnames}
          disabled={selectedIndex === tabMap.UPLOADING}
        >
          Select Files
        </Tab>
        <Tab
          className={tabClassnames}
          disabled={!hasFilesToUpload || selectedIndex === tabMap.UPLOADING}
        >
          Rename Files
        </Tab>
        <Tab
          className={tabClassnames}
          disabled={hasFilesToUpload || selectedIndex === tabMap.SELECT_FILES}
        >
          Upload
        </Tab>
      </Tab.List>
      <Tab.Panels>
        <Tab.Panel>
          {promptMessage && (
            <div className='text-center text-gray-700 text-lg font-medium'>{promptMessage}</div>
          )}

          <div className='flex justify-center items-center my-20'>
            <FileSelector onSelectFiles={onSelectFiles} />
          </div>
          {enableFilesDrop && (
            <div
              className='h-44 flex items-center justify-center border-4 border-dashed border-gray-300 text-gray-500'
              onDragOver={(e) => {
                e.preventDefault()
                const target = e.target as HTMLDivElement
                target.classList.add('border-blue-500')
              }}
              onDrop={(e) => {
                e.preventDefault()
                const files = e.dataTransfer.files

                if (multiple) {
                  setFilesToUpload(Array.from(files))
                } else {
                  setFilesToUpload([files[0]])
                }

                setSelectedIndex(tabMap.RENAME_FILES)
              }}
            >
              or drop files here
            </div>
          )}
        </Tab.Panel>
        <Tab.Panel className='min-h-[420px] max-h-[480px] overflow-auto'>
          <FilesRenameForm
            filesToUpload={filesToUpload}
            handleFilesRename={handleFilesRename}
            handleFileRemoval={handleFileRemoval}
            isUploading={isUploading}
            forcedTag={forcedTag}
          />
        </Tab.Panel>
        <Tab.Panel className='h-80 flex items-center justify-center'>
          <Spinner />
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  )
}
